import type { FetchResponse, User } from "shared-components";

import { actionHandler } from "../action-handler";
import { clientPost } from "../client";

import type {
  AnonymousSessionResponse,
  LoginRequest,
  RequestPasswordReset,
  ResendEmailRequest,
  ResetPasswordRequest,
  Session,
  SignUpRequest,
} from "./models";

export const login = actionHandler(async (payload: LoginRequest): Promise<FetchResponse<Session>> => {
  return clientPost({ path: "auth/login", payload });
});

export const logout = async (): Promise<FetchResponse<null>> => {
  return clientPost({ path: "auth/logout" });
};

export const requestPasswordReset = actionHandler(
  async (payload: RequestPasswordReset): Promise<FetchResponse<null>> => {
    return clientPost({ path: "auth/request_password_reset", payload });
  },
);

export const requestValidatePasswordReset = actionHandler(
  async (payload: {
    passwordResetCode: string;
  }): Promise<FetchResponse<null>> => {
    return clientPost({
      path: "auth/validate_password_reset_code",
      payload,
      returnStatusCode: true,
    });
  },
);

export const resetPassword = actionHandler(async (payload: ResetPasswordRequest): Promise<FetchResponse<null>> => {
  return clientPost({ path: "auth/reset_password", payload });
});

export const requestValidateCode = actionHandler(async (payload: { code: string }): Promise<FetchResponse<null>> => {
  return clientPost({
    path: "auth/validate_code",
    payload,
    returnStatusCode: true,
  });
});

export const signup = actionHandler(async (payload: SignUpRequest): Promise<FetchResponse<{ user: User }>> => {
  return clientPost({ path: "auth/signup", payload });
});

export const requestResendEmail = actionHandler(async (payload: ResendEmailRequest): Promise<FetchResponse<null>> => {
  return clientPost({ path: "auth/resend_email", payload });
});

export const resendFirstAccessCode = actionHandler(async (payload: { email: string }): Promise<FetchResponse<null>> => {
  return clientPost({ path: "auth/resend_first_access", payload });
});

const createAnonymousSession = actionHandler(async (): Promise<FetchResponse<AnonymousSessionResponse>> => {
  return clientPost({ path: "auth/anonymous-session" });
});

/**
 * Cache de promise no escopo de módulo para evitar chamadas duplicadas.
 * Persiste entre chamadas da função, permitindo o padrão singleton.
 */
let anonymousSessionPromise: Promise<FetchResponse<AnonymousSessionResponse>> | null = null;

/**
 * @description Singleton de promise que garante que exista uma sessão anônima.
 * Reutiliza a promise em andamento se chamada múltiplas vezes simultaneamente,
 * evitando requisições duplicadas ao backend e garantindo que apenas um cookie seja criado.
 */
export const ensureAnonymousSession = (): Promise<FetchResponse<AnonymousSessionResponse>> => {
  anonymousSessionPromise ??= createAnonymousSession(undefined).finally(() => {
    anonymousSessionPromise = null;
  });

  return anonymousSessionPromise;
};
